<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

// One proposed plan under an advisor answer: its steps, the approval (a
// second click that names what will happen), live progress while it runs,
// and undo once it has run. The steps' words come from the server; nothing
// here decides what a step does.
const props = defineProps({
  plan: { type: Object, required: true },
  busy: { type: Boolean, default: false },
});

const emit = defineEmits(['approve', 'undo']);

const { t } = useI18n();
const confirming = ref(false);
const sensitiveAck = ref(false);

const status = computed(() => props.plan.status);
const steps = computed(() => props.plan.steps || []);
const doneCount = computed(
  () => steps.value.filter(step => step.state === 'done').length
);
const isRunning = computed(() => ['running', 'undoing'].includes(status.value));
const canUndo = computed(
  () => ['done', 'failed'].includes(status.value) && doneCount.value > 0
);
const failedStep = computed(() =>
  steps.value.find(step => step.state === 'failed')
);
const sensitiveSteps = computed(() =>
  steps.value.filter(step => step.sensitive)
);
const hasSensitive = computed(() => sensitiveSteps.value.length > 0);
const irreversibleCount = computed(
  () => steps.value.filter(step => step.reversible === false).length
);
// A plan approved now that runs later (Saudi time, as the manager said it).
const runAt = computed(() =>
  props.plan.run_at
    ? new Date(props.plan.run_at).toLocaleString('ar-SA-u-nu-latn', {
        timeZone: 'Asia/Riyadh',
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : ''
);
const keptCount = computed(
  () => steps.value.filter(step => step.state === 'kept').length
);

const stateIcon = state =>
  ({
    done: 'i-lucide-check-circle-2 text-n-teal-11',
    failed: 'i-lucide-x-circle text-n-ruby-11',
    undone: 'i-lucide-undo-2 text-n-slate-11',
    skipped: 'i-lucide-minus-circle text-n-slate-10',
    kept: 'i-lucide-lock text-n-amber-11',
  })[state] || 'i-lucide-circle text-n-slate-9';

const approve = () => {
  if (hasSensitive.value && !sensitiveAck.value) return;
  confirming.value = false;
  emit('approve', {
    id: props.plan.id,
    confirmSensitive: hasSensitive.value && sensitiveAck.value,
  });
  sensitiveAck.value = false;
};

const cancel = () => {
  confirming.value = false;
  sensitiveAck.value = false;
};
</script>

<template>
  <div
    class="mt-2 flex flex-col gap-3 rounded-lg border border-n-blue-7 bg-n-blue-2 p-3"
  >
    <div class="flex items-center gap-2 text-sm font-medium text-n-blue-12">
      <span class="i-lucide-list-checks size-4" />
      {{ t('TELETEL_ADVISOR.PLAN.TITLE') }}
      <span
        class="ms-auto rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-normal text-n-slate-11"
      >
        {{ t(`TELETEL_ADVISOR.PLAN.STATUS.${status.toUpperCase()}`) }}
      </span>
    </div>

    <ol class="flex flex-col gap-1.5 text-sm">
      <li
        v-for="(step, i) in steps"
        :key="i"
        class="flex items-start gap-2"
        :class="{ 'opacity-60': step.state === 'skipped' }"
      >
        <span class="mt-0.5 size-4 shrink-0" :class="stateIcon(step.state)" />
        <span class="flex flex-col">
          <span class="flex flex-wrap items-center gap-1.5">
            {{
              t('TELETEL_ADVISOR.PLAN.STEP_LINE', {
                n: i + 1,
                summary: step.summary,
              })
            }}
            <span
              v-if="step.sensitive"
              class="inline-flex items-center gap-1 rounded bg-n-amber-3 px-1.5 text-xs text-n-amber-12"
            >
              <span class="i-lucide-triangle-alert size-3" />
              {{ t('TELETEL_ADVISOR.PLAN.SENSITIVE') }}
            </span>
            <span
              v-if="step.reversible === false"
              class="inline-flex items-center gap-1 rounded bg-n-ruby-3 px-1.5 text-xs text-n-ruby-12"
            >
              <span class="i-lucide-lock size-3" />
              {{ t('TELETEL_ADVISOR.PLAN.IRREVERSIBLE') }}
            </span>
          </span>
          <span
            v-if="step.sample && step.sample.length && status === 'proposed'"
            class="text-xs text-n-slate-11"
          >
            {{
              t('TELETEL_ADVISOR.PLAN.SAMPLE', {
                items: step.sample.join('، '),
              })
            }}
          </span>
          <span v-if="step.error" class="text-xs text-n-ruby-11">
            {{ step.error }}
          </span>
        </span>
      </li>
    </ol>

    <p v-if="status === 'proposed'" class="text-xs text-n-slate-11">
      {{ t('TELETEL_ADVISOR.PLAN.PROPOSED_NOTE') }}
      <template v-if="runAt">
        {{ t('TELETEL_ADVISOR.PLAN.RUNS_AT', { time: runAt }) }}
      </template>
    </p>
    <p
      v-else-if="status === 'scheduled'"
      class="flex items-center gap-2 text-xs text-n-blue-11"
    >
      <span class="i-lucide-calendar-clock size-3.5" />
      {{ t('TELETEL_ADVISOR.PLAN.SCHEDULED', { time: runAt }) }}
    </p>
    <p v-else-if="status === 'cancelled'" class="text-xs text-n-slate-11">
      {{ t('TELETEL_ADVISOR.PLAN.CANCELLED') }}
    </p>
    <p
      v-else-if="isRunning"
      class="flex items-center gap-2 text-xs text-n-slate-11"
    >
      <span class="i-lucide-loader-circle size-3.5 animate-spin" />
      {{
        t(
          status === 'undoing'
            ? 'TELETEL_ADVISOR.PLAN.UNDOING'
            : 'TELETEL_ADVISOR.PLAN.RUNNING'
        )
      }}
    </p>
    <p v-else-if="status === 'done'" class="text-xs text-n-teal-11">
      {{ t('TELETEL_ADVISOR.PLAN.DONE', { n: doneCount }) }}
    </p>
    <p v-else-if="status === 'failed'" class="text-xs text-n-ruby-11">
      {{
        t('TELETEL_ADVISOR.PLAN.FAILED', {
          n: doneCount,
          step: failedStep ? failedStep.summary : '',
        })
      }}
    </p>
    <p v-else-if="status === 'undone'" class="text-xs text-n-slate-11">
      {{ t('TELETEL_ADVISOR.PLAN.UNDONE') }}
    </p>
    <p v-if="keptCount > 0" class="text-xs text-n-amber-11">
      {{ t('TELETEL_ADVISOR.PLAN.KEPT', { n: keptCount }) }}
    </p>

    <div v-if="status === 'proposed' && !confirming" class="flex gap-2">
      <NextButton
        blue
        sm
        icon="i-lucide-play"
        :label="t('TELETEL_ADVISOR.PLAN.APPROVE')"
        :disabled="busy"
        @click="confirming = true"
      />
    </div>
    <div
      v-else-if="status === 'proposed' && confirming"
      class="flex flex-col gap-2 rounded-md border border-n-weak bg-n-solid-1 p-2"
    >
      <p class="text-xs text-n-slate-12">
        {{
          irreversibleCount
            ? t('TELETEL_ADVISOR.PLAN.CONFIRM_WITH_IRREVERSIBLE', {
                n: steps.length,
                k: irreversibleCount,
              })
            : t('TELETEL_ADVISOR.PLAN.CONFIRM', { n: steps.length })
        }}
      </p>
      <label
        v-if="hasSensitive"
        class="flex items-start gap-2 rounded-md border border-n-amber-6 bg-n-amber-2 p-2 text-xs text-n-amber-12"
      >
        <input v-model="sensitiveAck" type="checkbox" class="mt-0.5" />
        <span>
          {{
            t('TELETEL_ADVISOR.PLAN.SENSITIVE_CONFIRM', {
              n: sensitiveSteps.length,
            })
          }}
        </span>
      </label>
      <div class="flex gap-2">
        <NextButton
          blue
          sm
          :label="t('TELETEL_ADVISOR.PLAN.CONFIRM_YES')"
          :disabled="busy || (hasSensitive && !sensitiveAck)"
          @click="approve"
        />
        <NextButton
          slate
          faded
          sm
          :label="t('TELETEL_ADVISOR.PLAN.CONFIRM_NO')"
          @click="cancel"
        />
      </div>
    </div>
    <div v-else-if="status === 'scheduled'" class="flex gap-2">
      <NextButton
        slate
        faded
        sm
        icon="i-lucide-calendar-x"
        :label="t('TELETEL_ADVISOR.PLAN.CANCEL_SCHEDULE')"
        :disabled="busy"
        @click="emit('undo', plan.id)"
      />
    </div>
    <div v-else-if="canUndo" class="flex gap-2">
      <NextButton
        slate
        faded
        sm
        icon="i-lucide-undo-2"
        :label="t('TELETEL_ADVISOR.PLAN.UNDO')"
        :disabled="busy"
        @click="emit('undo', plan.id)"
      />
    </div>
  </div>
</template>
